import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  UserPlusIcon,
  EnvelopeIcon,
  KeyIcon,
  PhoneIcon,
  UserIcon,
  ShieldCheckIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import api from '../utils/api';

const ROLES = [
  { value: 'viewer', label: 'Viewer', description: 'View alerts and incidents only' },
  { value: 'supervisor', label: 'Supervisor', description: 'Acknowledge alerts and create incidents' },
  { value: 'safety_officer', label: 'Safety Officer', description: 'Full incident and alert management' },
  { value: 'site_manager', label: 'Site Manager', description: 'Manage sites and users' },
  { value: 'org_admin', label: 'Organization Admin', description: 'Full organization control' },
];

const ROLE_DEFAULTS = {
  viewer: { alerts: ['view'], incidents: ['view'], cameras: ['view'], reports: ['view'] },
  supervisor: {
    alerts: ['view', 'acknowledge'],
    incidents: ['view', 'create'],
    cameras: ['view'],
    reports: ['view', 'export'],
  },
  safety_officer: {
    alerts: ['view', 'acknowledge', 'resolve'],
    incidents: ['view', 'create', 'edit', 'close'],
    cameras: ['view'],
    reports: ['view', 'export'],
  },
  site_manager: {
    alerts: ['view', 'acknowledge', 'resolve'],
    incidents: ['view', 'create', 'edit', 'close'],
    cameras: ['view', 'configure'],
    reports: ['view', 'export'],
  },
  org_admin: {
    alerts: ['view', 'acknowledge', 'resolve'],
    incidents: ['view', 'create', 'edit', 'close'],
    cameras: ['view', 'configure'],
    reports: ['view', 'export'],
    users: ['view', 'create', 'edit', 'delete'],
  },
};

export default function UserCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'viewer',
    siteAccess: [],
    customPermissions: false,
    notificationPrefs: {
      email: { critical: true, high: true, medium: false },
      push: { critical: true, high: true, medium: true },
      sms: { critical: true, high: false },
    },
  });

  // Check authorization
  useEffect(() => {
    if (!['org_admin', 'super_admin'].includes(user?.role)) {
      toast.error('You do not have permission to create users');
      navigate('/dashboard');
    }
    fetchSites();
  }, [user, navigate]);

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Invalid email format';

    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8)
      newErrors.password = 'Password must be at least 8 characters';

    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match';

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const permissions = formData.customPermissions
        ? {} // User will set custom permissions in next step
        : ROLE_DEFAULTS[formData.role];

      const payload = {
        email: formData.email.toLowerCase(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim() || null,
        role: formData.role,
        siteAccess: formData.siteAccess.length > 0 ? formData.siteAccess : null,
        permissions,
        notificationPrefs: formData.notificationPrefs,
      };

      const response = await api.post('/users/register', payload);

      toast.success(`User created successfully: ${response.data.email}`);
      navigate('/users', { state: { userId: response.data.id } });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to create user';
      toast.error(errorMessage);
      console.error('Error creating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = (newRole) => {
    setFormData({
      ...formData,
      role: newRole,
      customPermissions: false,
    });
  };

  const toggleSiteAccess = (siteId) => {
    setFormData({
      ...formData,
      siteAccess: formData.siteAccess.includes(siteId)
        ? formData.siteAccess.filter(id => id !== siteId)
        : [...formData.siteAccess, siteId],
    });
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <UserPlusIcon className="w-6 h-6" />
            Create User
          </h1>
          <p className="page-description">Add a new user to your organization</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Basic Information */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            Basic Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={clsx('input', errors.firstName && 'border-red-500')}
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-sm text-red-500 mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={clsx('input', errors.lastName && 'border-red-500')}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-sm text-red-500 mt-1">{errors.lastName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Email *
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={clsx('input pl-10', errors.email && 'border-red-500')}
                  placeholder="john@example.com"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Phone
              </label>
              <div className="relative">
                <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input pl-10"
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Account Security */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <KeyIcon className="w-5 h-5" />
            Account Security
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Password *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={clsx('input', errors.password && 'border-red-500')}
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="text-sm text-red-500 mt-1">{errors.password}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={clsx('input', errors.confirmPassword && 'border-red-500')}
                placeholder="••••••••"
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-500 mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
        </div>

        {/* Role Selection */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ShieldCheckIcon className="w-5 h-5" />
            Role & Permissions
          </h2>

          <div className="space-y-3 mb-6">
            {ROLES.map((roleOption) => (
              <button
                key={roleOption.value}
                type="button"
                onClick={() => handleRoleChange(roleOption.value)}
                className={clsx(
                  'w-full p-4 rounded-xl border-2 text-left transition-all duration-200',
                  formData.role === roleOption.value
                    ? 'border-[var(--theme-500)] bg-[var(--theme-50)]'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                )}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{roleOption.label}</p>
                    <p className="text-sm text-slate-500">{roleOption.description}</p>
                  </div>
                  <div className={clsx(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                    formData.role === roleOption.value
                      ? 'border-[var(--theme-500)] bg-[var(--theme-500)]'
                      : 'border-slate-300'
                  )}>
                    {formData.role === roleOption.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <input
              type="checkbox"
              checked={formData.customPermissions}
              onChange={(e) => setFormData({ ...formData, customPermissions: e.target.checked })}
              className="w-4 h-4 rounded accent-[var(--theme-600)]"
            />
            <span className="text-sm font-medium text-slate-700">
              Customize permissions after creation
            </span>
          </label>
        </div>

        {/* Site Access */}
        {sites.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <MapIcon className="w-5 h-5" />
              Site Access
            </h2>

            <p className="text-sm text-slate-600 mb-4">
              Leave empty to grant access to all sites
            </p>

            <div className="space-y-2">
              {sites.map((site) => (
                <label
                  key={site.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.siteAccess.includes(site.id)}
                    onChange={() => toggleSiteAccess(site.id)}
                    className="w-4 h-4 rounded accent-[var(--theme-600)]"
                  />
                  <div>
                    <p className="font-medium text-slate-800">{site.name}</p>
                    {site.city && (
                      <p className="text-xs text-slate-500">{site.city}, {site.state}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Notification Preferences */}
        <div className="glass-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Notification Preferences
          </h2>

          <div className="space-y-4">
            {['email', 'push', 'sms'].map((channel) => (
              <div key={channel} className="p-4 border border-slate-200 rounded-lg">
                <h3 className="font-medium text-slate-700 capitalize mb-3">{channel}</h3>
                <div className="space-y-2">
                  {['critical', 'high', 'medium'].map((severity) => (
                    <label key={severity} className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={formData.notificationPrefs[channel]?.[severity] || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            notificationPrefs: {
                              ...formData.notificationPrefs,
                              [channel]: {
                                ...formData.notificationPrefs[channel],
                                [severity]: e.target.checked,
                              },
                            },
                          })
                        }
                        className="w-4 h-4 rounded accent-[var(--theme-600)]"
                      />
                      <span className="text-sm text-slate-600 capitalize">{severity} Alerts</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-3 mb-6">
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary flex-1"
          >
            {isLoading ? 'Creating...' : 'Create User'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="btn btn-secondary flex-1"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
